import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import GroupIcon from '@mui/icons-material/Group';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

const PLAN_OPTIONS = [
  { key: 'plan_1m', label: '1 Month Access', price: '$16' },
  { key: 'plan_3m', label: '3 Months Access', price: '$30' },
  { key: 'plan_6m', label: '6 Months Access', price: '$50' },
  { key: 'plan_12m', label: '12 Months / 1 Year', price: '$79.99' },
  { key: 'plan_monthly', label: 'Monthly Until Canceled', price: '$8.99/mo' },
];

const formatDateTime = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const identityLabel = (entry) => {
  const userId = String(entry?.user_id || '').trim();
  const email = String(entry?.email || '').trim();
  const displayName = String(entry?.display_name || '').trim();

  if (displayName && email) return `${displayName} (${email})`;
  if (displayName && userId) return `${displayName} (${userId})`;
  if (email) return email;
  return userId || 'Unknown user';
};

const AccountSettings = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const runtimeEnv = window.__RUNTIME_ENV__ || {};
  const authAudience = String(runtimeEnv.AUTH0_AUDIENCE || process.env.REACT_APP_AUTH0_AUDIENCE || '').trim();

  const [authToken, setAuthToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [entitlement, setEntitlement] = useState(null);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteUpdatedAt, setInviteUpdatedAt] = useState(null);
  const [followIdentifier, setFollowIdentifier] = useState('');
  const [connectCode, setConnectCode] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [showQr, setShowQr] = useState(false);
  const [checkoutPlanKey, setCheckoutPlanKey] = useState('');

  const normalizedEmail = String(user?.email || '').trim().toLowerCase();
  const userId = String(user?.sub || '').trim();
  const userName = String(user?.name || '').trim();

  const getApiAccessToken = useCallback(async () => {
    const withAudience = {
      scope: 'openid profile email',
      ...(authAudience ? { audience: authAudience } : {}),
    };

    try {
      return String((await getAccessTokenSilently({ authorizationParams: withAudience })) || '');
    } catch (firstError) {
      if (!authAudience) {
        throw firstError;
      }

      const withoutAudience = { scope: 'openid profile email' };
      return String((await getAccessTokenSilently({ authorizationParams: withoutAudience })) || '');
    }
  }, [authAudience, getAccessTokenSilently]);

  useEffect(() => {
    let active = true;

    const loadToken = async () => {
      if (!isAuthenticated) {
        if (active) {
          setAuthToken('');
        }
        return;
      }

      try {
        const token = await getApiAccessToken();
        if (active) {
          setAuthToken(String(token || ''));
        }
      } catch (tokenError) {
        if (active) {
          setAuthToken('');
        }
      }
    };

    loadToken();

    return () => {
      active = false;
    };
  }, [getApiAccessToken, isAuthenticated]);

  const buildRequestHeaders = useCallback(async ({ forceRefreshToken = false } = {}) => {
    const headers = {
      'X-User-Id': userId || 'anonymous',
      ...(normalizedEmail ? { 'X-User-Email': normalizedEmail } : {}),
      ...(userName ? { 'X-User-Name': userName } : {}),
    };

    if (!isAuthenticated) {
      return headers;
    }

    let token = forceRefreshToken ? '' : authToken;
    if (!token) {
      token = await getApiAccessToken();
      if (token) {
        setAuthToken(token);
      }
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [authToken, getApiAccessToken, isAuthenticated, normalizedEmail, userId, userName]);

  const fetchWithAuthRetry = useCallback(async (url, init = {}) => {
    const firstAuthHeaders = await buildRequestHeaders();
    const firstHeaders = {
      ...(init.headers || {}),
      ...firstAuthHeaders,
    };

    let response = await fetch(url, {
      ...init,
      headers: firstHeaders,
    });

    if (response.status !== 401 || !isAuthenticated) {
      return response;
    }

    const refreshedAuthHeaders = await buildRequestHeaders({ forceRefreshToken: true });
    const retryHeaders = {
      ...(init.headers || {}),
      ...refreshedAuthHeaders,
    };

    response = await fetch(url, {
      ...init,
      headers: retryHeaders,
    });

    return response;
  }, [buildRequestHeaders, isAuthenticated]);

  const loadSettings = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Please sign in to manage account settings.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [entitlementRes, followingRes, followersRes, inviteCodeRes] = await Promise.all([
        fetchWithAuthRetry(`${API_BASE_URL}/api/billing/entitlement?ts=${Date.now()}`),
        fetchWithAuthRetry(`${API_BASE_URL}/api/social/following`),
        fetchWithAuthRetry(`${API_BASE_URL}/api/social/followers`),
        fetchWithAuthRetry(`${API_BASE_URL}/api/social/invite-code`),
      ]);

      const warnings = [];

      if (followingRes.ok) {
        const followingPayload = await followingRes.json();
        setFollowing(Array.isArray(followingPayload) ? followingPayload : []);
      } else {
        warnings.push(`Following request failed (${followingRes.status})`);
      }

      if (followersRes.ok) {
        const followersPayload = await followersRes.json();
        setFollowers(Array.isArray(followersPayload) ? followersPayload : []);
      } else {
        warnings.push(`Followers request failed (${followersRes.status})`);
      }

      if (inviteCodeRes.ok) {
        const invitePayload = await inviteCodeRes.json();
        setInviteCode(String(invitePayload?.code || ''));
        setInviteUpdatedAt(invitePayload?.updatedAt || null);
      } else {
        warnings.push(`Invite code request failed (${inviteCodeRes.status})`);
      }

      if (entitlementRes.ok) {
        const entitlementPayload = await entitlementRes.json();
        setEntitlement(entitlementPayload || null);
      } else {
        setEntitlement(null);
        warnings.push(`Entitlement request failed (${entitlementRes.status})`);
      }

      if (warnings.length > 0) {
        setError(warnings.join(' | '));
      }
    } catch (loadError) {
      setError(loadError.message || 'Failed to load account settings');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuthRetry, isAuthenticated]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const codeFromQuery = String(params.get('connect') || '').trim().toUpperCase();
    if (codeFromQuery) {
      setConnectCode(codeFromQuery);
    }
  }, []);

  const followByIdentifier = async () => {
    const identifier = String(followIdentifier || '').trim();
    if (!identifier) return;

    setBusyKey('follow-identifier');
    setError('');
    setNotice('');

    try {
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/social/following`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Follow request failed (${response.status})`);
      }

      setFollowIdentifier('');
      setNotice('Connection added successfully.');
      await loadSettings();
    } catch (followError) {
      setError(followError.message || 'Failed to follow user');
    } finally {
      setBusyKey('');
    }
  };

  const followByCode = async () => {
    const code = String(connectCode || '').trim().toUpperCase();
    if (!code) return;

    setBusyKey('follow-code');
    setError('');
    setNotice('');

    try {
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/social/follow-by-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Connect request failed (${response.status})`);
      }

      setConnectCode('');
      setNotice('Connected using invite code.');
      await loadSettings();
    } catch (followError) {
      setError(followError.message || 'Failed to connect by invite code');
    } finally {
      setBusyKey('');
    }
  };

  const unfollow = async (identifier) => {
    const value = String(identifier || '').trim();
    if (!value) return;

    setBusyKey(`unfollow:${value}`);
    setError('');
    setNotice('');

    try {
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/social/following/${encodeURIComponent(value)}`, {
        method: 'DELETE',
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Unfollow request failed (${response.status})`);
      }

      setNotice('Connection removed.');
      await loadSettings();
    } catch (unfollowError) {
      setError(unfollowError.message || 'Failed to remove connection');
    } finally {
      setBusyKey('');
    }
  };

  const regenerateInviteCode = async () => {
    setBusyKey('regenerate-code');
    setError('');
    setNotice('');

    try {
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/social/invite-code/regenerate`, {
        method: 'POST',
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Regenerate request failed (${response.status})`);
      }

      setInviteCode(String(payload?.code || ''));
      setInviteUpdatedAt(payload?.updatedAt || null);
      setNotice('Invite code regenerated. Share the new code with people you trust.');
    } catch (regenerateError) {
      setError(regenerateError.message || 'Failed to regenerate invite code');
    } finally {
      setBusyKey('');
    }
  };

  const startCheckout = async (planKey) => {
    setCheckoutPlanKey(planKey);
    setError('');
    setNotice('');

    try {
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planKey }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Checkout request failed (${response.status})`);
      }

      const checkoutUrl = String(payload?.url || '');
      if (!checkoutUrl) {
        throw new Error('Checkout URL missing from response');
      }

      window.location.assign(checkoutUrl);
    } catch (checkoutError) {
      setError(checkoutError.message || 'Failed to start checkout');
      setCheckoutPlanKey('');
    }
  };

  const openBillingPortal = async () => {
    setBusyKey('billing-portal');
    setError('');
    setNotice('');

    try {
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/billing/create-portal-session`, {
        method: 'POST',
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Billing portal request failed (${response.status})`);
      }

      const portalUrl = String(payload?.url || '');
      if (!portalUrl) {
        throw new Error('Billing portal URL missing from response');
      }

      window.location.assign(portalUrl);
    } catch (portalError) {
      setError(portalError.message || 'Failed to open billing portal');
    } finally {
      setBusyKey('');
    }
  };

  const shareLink = useMemo(() => {
    if (!inviteCode) return '';
    return `${window.location.origin}/account-settings?connect=${encodeURIComponent(inviteCode)}`;
  }, [inviteCode]);

  const qrCodeUrl = useMemo(() => {
    if (!shareLink) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(shareLink)}`;
  }, [shareLink]);

  const copyValue = async (value, copiedLabel) => {
    const text = String(value || '').trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${copiedLabel} copied to clipboard.`);
    } catch (copyError) {
      setError('Failed to copy value to clipboard');
    }
  };

  const entitlementInfo = entitlement?.entitlement || null;
  const hasActiveAccess = Boolean(entitlement?.hasActiveEntitlement);

  const goBackToApp = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 3 }}>
        <Button
          variant="outlined"
          onClick={goBackToApp}
          startIcon={<ArrowBackIcon />}
          sx={{ alignSelf: 'flex-start' }}
        >
          Back to App
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={22} />
          <Typography>Loading account settings...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ManageAccountsIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Account Settings
          </Typography>
        </Stack>
        <Button
          variant="outlined"
          onClick={goBackToApp}
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          Back to App
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage your account, subscription plan, and social connections for watchlist sharing and portfolio competitions.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Profile
            </Typography>
            <Typography variant="body2"><strong>Name:</strong> {userName || '-'}</Typography>
            <Typography variant="body2"><strong>Email:</strong> {normalizedEmail || '-'}</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}><strong>User ID:</strong> {userId || '-'}</Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Plan Management
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 1 }}>
              <Chip
                size="small"
                color={hasActiveAccess ? 'success' : 'default'}
                label={hasActiveAccess ? 'Active subscription' : 'No active subscription'}
              />
              {entitlementInfo?.plan_key && (
                <Chip size="small" variant="outlined" label={`Plan: ${entitlementInfo.plan_key}`} />
              )}
              {entitlementInfo?.ends_at && (
                <Chip size="small" variant="outlined" label={`Ends: ${formatDateTime(entitlementInfo.ends_at)}`} />
              )}
            </Stack>

            <Button
              variant="outlined"
              onClick={openBillingPortal}
              disabled={busyKey === 'billing-portal'}
              startIcon={busyKey === 'billing-portal' ? <CircularProgress size={14} /> : <ManageAccountsIcon />}
              sx={{ mb: 1.5 }}
            >
              Manage Billing Portal
            </Button>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Available Plans
            </Typography>
            <Grid container spacing={1}>
              {PLAN_OPTIONS.map((plan) => (
                <Grid item xs={12} sm={6} key={plan.key}>
                  <Paper variant="outlined" sx={{ p: 1.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{plan.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{plan.price}</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => startCheckout(plan.key)}
                        disabled={checkoutPlanKey === plan.key}
                      >
                        {checkoutPlanKey === plan.key ? 'Redirecting...' : 'Choose Plan'}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Connect With People
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Add people by identifier or invite code to share watchlists and compare portfolio performance.
            </Typography>
            <Stack spacing={1.25}>
              <TextField
                size="small"
                label="Follow by email or user ID"
                value={followIdentifier}
                onChange={(event) => setFollowIdentifier(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    followByIdentifier();
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={followByIdentifier}
                disabled={!followIdentifier.trim() || busyKey === 'follow-identifier'}
                startIcon={busyKey === 'follow-identifier' ? <CircularProgress size={14} /> : <GroupIcon />}
              >
                Follow by Identifier
              </Button>

              <Divider />

              <TextField
                size="small"
                label="Connect using invite code"
                value={connectCode}
                onChange={(event) => setConnectCode(event.target.value.toUpperCase())}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    followByCode();
                  }
                }}
              />
              <Button
                variant="contained"
                onClick={followByCode}
                disabled={!connectCode.trim() || busyKey === 'follow-code'}
                startIcon={busyKey === 'follow-code' ? <CircularProgress size={14} /> : <GroupIcon />}
              >
                Follow by Code
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Your Invite Code
              </Typography>
              <Button
                size="small"
                onClick={regenerateInviteCode}
                disabled={busyKey === 'regenerate-code'}
                startIcon={busyKey === 'regenerate-code' ? <CircularProgress size={14} /> : <AutorenewIcon />}
              >
                Regenerate
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
              <Chip color="primary" label={inviteCode || '-'} />
              <Button size="small" onClick={() => copyValue(inviteCode, 'Invite code')} startIcon={<ContentCopyIcon />}>Copy Code</Button>
              <Button size="small" onClick={() => copyValue(shareLink, 'Invite link')} startIcon={<ContentCopyIcon />}>Copy Link</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Last updated: {formatDateTime(inviteUpdatedAt)}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowQr((prev) => !prev)}
              startIcon={<QrCode2Icon />}
            >
              {showQr ? 'Hide QR' : 'Show QR'}
            </Button>
            {showQr && qrCodeUrl && (
              <Box sx={{ mt: 1.25 }}>
                <img src={qrCodeUrl} alt="Invite QR code" width="220" height="220" />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Share this QR to let someone follow you using your invite code.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Following ({following.length})
            </Typography>
            {following.length === 0 ? (
              <Typography variant="body2" color="text.secondary">You are not following anyone yet.</Typography>
            ) : (
              <List dense sx={{ p: 0 }}>
                {following.map((entry) => {
                  const userKey = String(entry?.user_id || '').trim();
                  return (
                    <ListItem
                      key={`following-${userKey}`}
                      sx={{ px: 0 }}
                      secondaryAction={
                        <Button
                          size="small"
                          color="error"
                          onClick={() => unfollow(userKey)}
                          disabled={busyKey === `unfollow:${userKey}`}
                          startIcon={<LogoutIcon fontSize="small" />}
                        >
                          Remove
                        </Button>
                      }
                    >
                      <ListItemText
                        primary={identityLabel(entry)}
                        secondary={`Followed ${formatDateTime(entry?.created_at)}`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Followers ({followers.length})
            </Typography>
            {followers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No followers yet.</Typography>
            ) : (
              <List dense sx={{ p: 0 }}>
                {followers.map((entry) => {
                  const followerKey = `${String(entry?.user_id || '').trim()}-${String(entry?.created_at || '').trim()}`;
                  return (
                  <ListItem key={`follower-${followerKey}`} sx={{ px: 0 }}>
                    <ListItemText
                      primary={identityLabel(entry)}
                      secondary={`Started following ${formatDateTime(entry?.created_at)}`}
                    />
                  </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountSettings;
